<template>
  <InvestorOnboardingStep>
    <View direction="column" gap="lg">
      <View direction="column" gap="sm">
        <View direction="row" gap="sm" align="center">
          <Text variant="h2">Invite your team</Text>
          <Badge label="Optional" variant="grey" size="small" />
        </View>
        <ul class="roles">
          <li v-for="role in roles" :key="role">
            <Text variant="body-small" color="secondary">
              <strong>{{ companyRoleLabels[role] }}:</strong>
              {{ companyRoleDescriptions[role] }}
            </Text>
          </li>
        </ul>
      </View>

      <View v-if="invited.length" direction="column" gap="sm">
        <TeamPersonRow
          v-for="invite in invited"
          :key="invite.id"
          :name="invite.email"
          details="Invite sent"
          :badges="[{ label: companyRoleLabels[invite.role], variant: companyRoleBadgeVariants[invite.role] }]"
        >
          <template #leading>
            <Avatar :user-name="invite.email" show-single-letter status="pending" size="medium" />
          </template>
        </TeamPersonRow>
      </View>

      <div ref="listRef" class="rows">
        <div v-for="draft in drafts" :key="draft.id" class="invite-row">
          <div class="address">
            <FormField :error="draftError(draft)">
              <Input
                v-model="draft.email"
                type="email"
                placeholder="teammate@company.com"
                :disabled="isSaving"
                data-testid="onboarding-invite-email-input"
                @blur="draft.touched = true"
                @keydown.enter="draft.touched = true"
                @paste="onPaste(draft, $event)"
              >
                <template #prefix>
                  <Icon class="email-icon" name="mail-01" size="small" />
                </template>
              </Input>
            </FormField>

            <Button
              v-if="pendingInviteId(draft)"
              variant="outline"
              size="small"
              :loading="resendingDraftId === draft.id"
              @click="resend(draft)"
            >
              Resend instead
            </Button>
          </div>

          <Select v-model="draft.role" class="role-select" aria-label="Role" :disabled="isSaving">
            <option v-for="role in roles" :key="role" :value="role">{{ companyRoleLabels[role] }}</option>
          </Select>

          <div class="row-remove">
            <IconButton
              v-if="drafts.length > 1"
              icon="x-close"
              variant="ghost"
              size="medium"
              aria-label="Remove"
              :disabled="isSaving"
              @click="remove(draft)"
            />
          </div>
        </div>

        <div>
          <Button variant="ghost" size="small" :disabled="isSaving" @click="addRow">
            <Icon name="plus" size="small" />
            Add another
          </Button>
        </div>
      </div>

      <Text variant="body-small" color="tertiary">
        Each invite link works for 7 days. You can invite more people later in Settings, under Team.
      </Text>

      <View justify="end" gap="sm">
        <Button variant="ghost" :disabled="isSaving" @click="goBack">Back</Button>
        <Button
          :variant="typed.length || invited.length ? 'primary' : 'ghost'"
          :disabled="!canContinue"
          :loading="isSaving"
          data-testid="onboarding-invite-forward-button"
          @click="next"
        >
          {{ forwardLabel }}
        </Button>
      </View>
    </View>
  </InvestorOnboardingStep>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import pluralize from 'pluralize';
import { computed, nextTick, ref } from 'vue';
import { useRouter } from 'vue-router';

import {
  Avatar,
  Badge,
  Button,
  FormField,
  Icon,
  IconButton,
  Input,
  Select,
  Text,
  View,
  useAlertsStore,
} from '@tallo/design-system';
import {
  CompanyRole,
  InviteDraft,
  companyRoleBadgeVariants,
  companyRoleDescriptions,
  companyRoleLabels,
  companyTeamQueryKey,
  useCompanyTeamQuery,
  useInviteDrafts,
  useResendInvite,
} from '@tallo/investor/company';
import {
  InvestorOnboardingStep as OnboardingStep,
  useOnboardingStep,
  useOnboardingStore,
} from '@tallo/investor/investor';
import { AppRouteNames } from '@tallo/investor/navigation';
import { TeamPersonRow } from '@tallo/investor/property';
import { escapeHtml } from '@tallo/utility';

import InvestorOnboardingStep from '../components/investor-onboarding-step.vue';

const store = useOnboardingStore();
const router = useRouter();
const alerts = useAlertsStore();
const queryCache = useQueryCache();
const resendInvite = useResendInvite();
// Coming back to this step lists who was already invited, rather than an empty form that invites them twice
const { pendingInvites } = useCompanyTeamQuery();
const { drafts, sent, typed, allSendable, draftError, pendingInviteId, add, remove, paste, sendAll } =
  useInviteDrafts();

const roles = [CompanyRole.MEMBER, CompanyRole.ADMIN];

const listRef = ref<HTMLElement | null>(null);
const resendingDraftId = ref<number | null>(null);

// Sent from here shows at once, without waiting for the roster to be read again
const invited = computed(() => [
  ...sent.value,
  ...pendingInvites.value.filter((invite) => !sent.value.some((mine) => mine.id === invite.id)),
]);

const forwardLabel = computed(() => {
  if (typed.value.length) return `Send ${typed.value.length} ${pluralize('invite', typed.value.length)}`;
  return invited.value.length ? 'Continue' : 'Skip for now';
});

// Leaving every row empty is an answer (skip); a typed address has to be sendable, and one the
// server refused has to be fixed or removed, before the step moves on
const { goBack, isSaving, canContinue, save, onContinue } = useOnboardingStep(() => allSendable.value);

async function next() {
  if (!typed.value.length) {
    await finish();
    return;
  }

  let outcome = null as Awaited<ReturnType<typeof sendAll>> | null;
  await save(async () => {
    try {
      outcome = await sendAll();
    } finally {
      // Whatever went out is on the roster now, even when part of the batch failed
      queryCache.invalidateQueries({ key: [companyTeamQueryKey] }).catch(() => undefined);
    }
  }, "We couldn't send those invites. Please try again.");

  // A refused row says why and stays; the step waits for it to be fixed or removed
  if (!outcome || outcome.refused) return;

  const delivered = outcome.sent;
  alerts.success(
    delivered.length === 1
      ? `Invite sent to <b>${escapeHtml(delivered[0].email)}</b>`
      : `${delivered.length} invites sent`,
  );
  await finish();
}

async function resend(draft: InviteDraft) {
  const inviteId = pendingInviteId(draft);
  if (!inviteId) return;

  resendingDraftId.value = draft.id;
  try {
    await resendInvite.mutateAsync(inviteId);
    alerts.success(`Invite resent to <b>${escapeHtml(draft.email.trim())}</b>`);
    // The invite was pending all along, so the list above has it; the row has nothing left to do
    remove(draft);
  } catch {
    // Already reported by the mutation's toast; the row keeps its refusal for another try
  } finally {
    resendingDraftId.value = null;
  }
}

async function addRow() {
  add();
  await nextTick();
  const inputs = listRef.value?.querySelectorAll<HTMLInputElement>('.invite-row input[type="email"]');
  inputs?.[inputs.length - 1]?.focus();
}

function onPaste(draft: InviteDraft, event: ClipboardEvent) {
  if (paste(draft, event.clipboardData?.getData('text') ?? '')) event.preventDefault();
}

// Sending, skipping and continuing all finish the step: the server counts a skipped step as answered
async function finish() {
  await save(async () => {
    await store.saveStep(OnboardingStep.InviteTeam, {});
    router.push({ name: AppRouteNames.INVESTOR_ONBOARDING_RENTAL_STRATEGY });
  }, "We couldn't save this step. Please try again.");
}

onContinue(next);
</script>

<style lang="scss" scoped>
.roles {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-xs);
  margin: 0;
  padding-left: var(--t-spacing-lg);
}

.rows {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-sm);
}

.invite-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 8.5rem 2.5rem;
  gap: var(--t-spacing-sm);
  align-items: start;
}

.address {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-sm);

  // The field fills the column; only the resend button hugs its text
  > .button {
    align-self: flex-start;
  }
}

.role-select {
  width: 100%;
}

// Keeps the column when a lone row has nothing to remove, so the fields never jump sideways
.row-remove {
  display: flex;
  align-items: center;
  height: var(--t-spacing-xxl);
}

.email-icon {
  color: var(--t-text-color-tertiary);
}
</style>
